import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AnalyticsChart from "@/components/AnalyticsChart";

function Bar({ label, value, max, isNA = false }){
  const width = max && !isNA ? Math.max(2, (value/max)*100) : 0;
  return (
    <div className="space-y-1" data-testid={`metric-bar-${label}`}>
      <div className="text-sm">{label}: {isNA ? 'N/A' : value.toLocaleString()}</div>
      {!isNA && (
        <div className="w-full bg-neutral-200 h-2 rounded">
          <div className="h-2 rounded" style={{ width: `${width}%`, background: "#0a2a66" }} />
        </div>
      )}
    </div>
  );
}

export default function Analytics(){
  const { id } = useParams();
  const navigate = useNavigate();
  const [data,setData] = useState({});
  const [err,setErr] = useState("");
  const [campaignStats,setCampaignStats]=useState([]);
  const [campaignInfo,setCampaignInfo]=useState(null);
  const [exporting,setExporting]=useState(false);
  const [token, setToken] = useState(null);
  
  useEffect(() => {
    // Get token for chart component
    const storedToken = localStorage.getItem('token');
    setToken(storedToken);
  }, []);
  
  useEffect(()=>{ 
    (async()=>{ 
      try{ 
        const r = await api.get('/analytics', { params: id ? { campaign_id: id } : {} }); 
        setData(r.data?.total || {}); 
        setCampaignStats(r.data?.campaigns || []);
        
        // Get campaign info to check if data collection is enabled
        if (id) {
          try {
            const campRes = await api.get(`/campaigns/${id}`);
            setCampaignInfo(campRes.data);
          } catch (e) {
            console.error("Failed to load campaign info:", e);
          }
        }
      } catch(e){ 
        console.error("Analytics error:", e);
        setErr("Failed to load analytics"); 
      } 
    })(); 
  },[id]);
  
  const exportCSV = async () => {
    setExporting(true);
    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert("Please log in to export data.");
        setExporting(false);
        return;
      }
      
      // Build the URL with params
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const params = id ? `?campaign_id=${id}` : '';
      const url = `${baseUrl}/api/analytics/export${params}`;
      
      // Fetch with auth header
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Try opening in new window first (works better in preview environments)
      const newWindow = window.open(downloadUrl, '_blank');
      if (!newWindow) {
        // Fallback to link click method
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `analytics_${id ? id.slice(0,8) : 'all'}.csv`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    } catch (e) {
      console.error("Export error:", e);
      alert("Failed to export CSV. Please try again.");
    } finally {
      setExporting(false);
    }
  };
  
  const dataCollectionEnabled = campaignInfo?.data_collection !== false;
  const leadsCount = data.leads || 0;
  const max = Math.max(1, data.generates || 0, leadsCount);
  
  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Analytics {campaignInfo?.title ? `- ${campaignInfo.title}` : ''}
          </h1>
          <p className="text-sm text-gray-500">Track campaign performance and engagement.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="analytics-back" variant="outline" onClick={()=>navigate('/campaigns')}>Back to Campaigns</Button>
          <Button data-testid="analytics-export" variant="outline" onClick={exportCSV} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>Total Analytics</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {err && (<div className="text-red-600 text-sm" data-testid="analytics-error">{err}</div>)}
          {Object.keys(data).length === 0 && !err && (<div data-testid="analytics-empty">No activity yet. Share your campaign widget to start tracking!</div>)}
          {data.generates !== undefined && (<Bar label="Emails Generated" value={data.generates} max={max} />)}
          <Bar label="Leads Generated" value={leadsCount} max={max} isNA={!dataCollectionEnabled} />
        </CardContent>
      </Card>
      
      {/* Time-series Chart */}
      {token && (
        <div className="mt-4">
          <AnalyticsChart token={token} campaignId={id} />
        </div>
      )}
      
      {campaignStats.length > 0 && (
        <Card className="mt-4">
          <CardHeader><CardTitle>Campaign Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {campaignStats.map((camp) => (
                <div key={camp.campaign_id} className="border-b pb-3 last:border-b-0">
                  <div className="font-medium mb-2">{camp.campaign_title}</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Generated: {camp.generates}</div>
                    <div>Leads: {camp.leads}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
